import { useEffect, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { X, ClipboardList, Paperclip, Trash2 } from 'lucide-react';
import { overlayDismiss } from '../../../lib/overlayDismiss';

/**
 * LeadFormModal — 收單情境 dialog
 *
 * 兩種輸入(可單用、可併用):
 *   1. 自然語言情境(textarea) — 一句話告訴 AI 什麼時候收什麼資料
 *   2. 收單文本/指示文件 — docx / pdf / 圖片(SOP / 規範文檔)
 *
 * sendMessage → `add-form-collection` skill,AI 自動拆解觸發 + 欄位 + 套用收單規範。
 */
export function LeadFormModal({ open, onClose, onApply, isStreaming, avatarName }) {
    const { t } = useTranslation('wsApi');
    const [text, setText] = useState('');
    const [files, setFiles] = useState([]);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!open) {
            setText('');
            setFiles([]);
        }
    }, [open]);

    if (!open) return null;

    const addFiles = (incoming) => {
        if (!incoming?.length) return;
        const next = [...files];
        for (const f of incoming) {
            if (!next.some((x) => x.name === f.name && x.size === f.size)) next.push(f);
        }
        setFiles(next);
    };
    const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

    const canApply = text.trim().length > 5 || files.length > 0;

    const handleApply = () => {
        if (isStreaming || !canApply) return;

        const fileLines = files.map((f, i) => `  ${i + 1}. ${f.name} (${Math.round(f.size / 1024)} KB)`).join('\n');

        const lines = [
            `請用 add-form-collection skill 為這個客服 (${avatarName || 'avatar'}) 新增收單情境。`,
            '',
        ];
        if (text.trim()) {
            lines.push('【需求(自然語言)】');
            lines.push(text.trim());
            lines.push('');
        }
        if (files.length) {
            lines.push('【附上的收單文本/指示文件】');
            lines.push(fileLines);
            lines.push('(docx 用 pandoc 轉文字;pdf / 圖片用 Read tool vision 讀。)');
            lines.push('');
        }
        lines.push('請自動:');
        lines.push('1. 從上面材料拆解出**觸發情境**(什麼時候要開始收單)');
        lines.push('2. 識別需要收集的**欄位**(含適當的 type:text / phone / email / date / number)');
        lines.push('3. 套用標準 IVR 收單規範:');
        lines.push('   - **一輪一題**(禁止「請問姓名 + 電話?」這種串問)');
        lines.push('   - 格式錯誤(phone 太短、email 沒 @)→ 引導重輸,不硬存');
        lines.push('   - 所有欄位收齊 → **read-back confirm** 後才 emit save_lead');
        lines.push('4. 加 4-5 對 few-shot 範例對話(含一對展示格式錯誤的處理)');
        lines.push('5. 全程用台灣口語親切感(您好~、沒問題~、讓我幫您)');
        lines.push('6. 完成後用商家口吻摘要:加了哪些欄位、什麼時候會觸發(不要貼 JSON)');

        const dParts = [];
        if (text.trim()) dParts.push(text.trim().slice(0, 60) + (text.trim().length > 60 ? '…' : ''));
        if (files.length) dParts.push(t('lead.dpartFiles', { n: files.length }));
        const displayText = t('lead.displayText', { parts: dParts.join(' ') });

        onApply(lines.join('\n'), { files: files.length ? files : undefined, displayText });
        onClose();
    };

    return (
        <div
            className="fixed inset-0 bg-ink-900/30 backdrop-blur-sm z-50 grid place-items-center p-4"
            {...overlayDismiss(onClose)}
        >
            <div
                className="bg-cream-50 border border-cream-200 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-400 inline-flex items-center gap-1.5">
                            <ClipboardList size={11} /> collect form setup
                        </div>
                        <h2 className="heading-display text-lg font-semibold text-ink-900 mt-0.5">{t('lead.title')}</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="size-8 inline-flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-cream-100 transition"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5 flex-1 overflow-y-auto space-y-4">
                    <div className="rounded-md bg-cream-100/60 border border-cream-200 px-3 py-2 text-xs text-ink-500 leading-relaxed">
                        <Trans i18nKey="lead.intro" t={t}>
                            告訴客服 <strong className="text-ink-700">什麼情況下要收什麼資料</strong>,或上傳<strong className="text-ink-700">收單文本/指示文件</strong>(SOP / 規範) —
                            AI 會自動拆解觸發 + 欄位 + 加範例對話 + 套用收單規範。
                        </Trans>
                    </div>

                    {/* 收單需求文字 */}
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2">{t('lead.reqLabel')}</div>
                        <textarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            rows={6}
                            placeholder={t('lead.reqPlaceholder')}
                            className="w-full resize-none px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white font-sans leading-relaxed"
                            autoFocus
                        />
                    </div>

                    {/* 文件上傳 */}
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2 inline-flex items-center gap-1.5">
                            <Paperclip size={11} /> {t('lead.filesLabel')}
                        </div>
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="w-full px-3 py-2.5 text-sm border border-dashed border-cream-300 hover:border-accent-400 hover:bg-cream-50 rounded-md bg-white text-ink-600 transition text-left"
                        >
                            {files.length === 0 ? t('lead.filePickEmpty') : t('lead.filePickMore', { n: files.length })}
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ''; }}
                            accept=".pdf,.docx,.doc,.txt,.md,.png,.jpg,.jpeg,.webp,.xlsx,.csv"
                            className="hidden"
                        />
                        {files.length > 0 && (
                            <ul className="mt-2 space-y-1">
                                {files.map((f, i) => (
                                    <li key={i} className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-white border border-cream-200 rounded text-xs">
                                        <span className="truncate text-ink-700">
                                            {f.name} <span className="text-ink-400 font-mono">({Math.round(f.size / 1024)} KB)</span>
                                        </span>
                                        <button onClick={() => removeFile(i)} className="size-5 inline-flex items-center justify-center text-ink-400 hover:text-danger-500" title={t('common:actions.delete')}>
                                            <Trash2 size={11} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-cream-200">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm rounded-md text-ink-700 hover:bg-cream-100 transition"
                    >
                        {t('common:actions.cancel')}
                    </button>
                    <button
                        onClick={handleApply}
                        disabled={isStreaming || !canApply}
                        className="px-4 py-2 text-sm rounded-md bg-ink-900 text-white hover:bg-ink-700 transition disabled:bg-cream-300 disabled:cursor-not-allowed"
                    >
                        {t('lead.apply')}
                    </button>
                </div>
            </div>
        </div>
    );
}
